<template>
  <section class="showcase-block services-block">
    <header class="section-head">
      <p class="section-kicker">
        <i class="fa-solid fa-store" aria-hidden="true" />
        Event ecosystem
      </p>
      <h2>Service Provider Slots for Better Events</h2>
      <p class="lead">
        Organizers open slots for vendors and officials so match day feels complete — from sound to safety.
      </p>
    </header>

    <div class="showcase-grid">
      <article
        v-for="service in services"
        :key="service.title"
        class="showcase-card"
        :style="{ '--card-accent': service.accent }"
      >
        <div class="showcase-card__media">
          <img
            :src="service.image"
            :alt="service.title"
            loading="lazy"
            decoding="async"
          />
          <div class="showcase-card__overlay" aria-hidden="true" />
          <span class="showcase-card__icon">
            <i :class="service.icon" aria-hidden="true" />
          </span>
        </div>
        <div class="showcase-card__body">
          <h3>{{ service.title }}</h3>
          <p>{{ service.description }}</p>
        </div>
      </article>
    </div>
  </section>

  <section class="showcase-block sports-block">
    <header class="section-head">
      <p class="section-kicker">
        <i class="fa-solid fa-medal" aria-hidden="true" />
        Multi-sport future
      </p>
      <h2>Growing Beyond Football</h2>
      <p class="lead expansion-lead">
        Tournament-creation tools for these sports are coming soon — the same IPA community, more games.
      </p>
    </header>

    <div class="showcase-grid showcase-grid--sports">
      <article
        v-for="sport in sports"
        :key="sport.name"
        class="showcase-card showcase-card--sport"
        :style="{ '--card-accent': sport.accent }"
      >
        <div class="showcase-card__media">
          <img
            :src="sport.image"
            :alt="`${sport.name} tournament`"
            loading="lazy"
            decoding="async"
          />
          <div class="showcase-card__overlay" aria-hidden="true" />
          <span class="showcase-card__badge">Coming soon</span>
          <span class="showcase-card__sport-label">{{ sport.name }}</span>
        </div>
        <div class="showcase-card__body">
          <h3>{{ sport.name }}</h3>
          <p>
            Organizers will soon create and manage {{ sport.name.toLowerCase() }} tournaments on IPA.
          </p>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import foodImage from "@/assets/images/tournament/smocha.png";
import soundImage from "@/assets/images/tournament/publicaddress.jpg";
import emergencyImage from "@/assets/images/tournament/firstaid.jpg";
import officialImage from "@/assets/images/tournament/official.jpg";

import rugbyImage from "@/assets/images/match/rugby.jpg";
import volleyballImage from "@/assets/images/match/volleyball2.jpg";
import tennisImage from "@/assets/images/match/tennis.jpg";
import basketballImage from "@/assets/images/match/basketball-court.jpg";

const services = [
  {
    title: "Food service",
    image: foodImage,
    icon: "fa-solid fa-burger",
    accent: "#f59e0b",
    description: "Vendors claim open slots and serve players, fans, and officials all day.",
  },
  {
    title: "Sound systems",
    image: soundImage,
    icon: "fa-solid fa-volume-high",
    accent: "#6366f1",
    description: "PA crews keep announcements, music, and match-day energy on point.",
  },
  {
    title: "Emergency services",
    image: emergencyImage,
    icon: "fa-solid fa-kit-medical",
    accent: "#ef4444",
    description: "First-aid and emergency support on standby for serious injuries.",
  },
  {
    title: "Match officiating",
    image: officialImage,
    icon: "fa-solid fa-whistle",
    accent: "#0c6b3d",
    description:
      "Built-in officiating slots — registered officials claim open roles for your fixtures.",
  },
];

const sports = [
  { name: "Rugby", image: rugbyImage, accent: "#15803d" },
  { name: "Volleyball", image: volleyballImage, accent: "#2563eb" },
  { name: "Tennis", image: tennisImage, accent: "#ca8a04" },
  { name: "Basketball", image: basketballImage, accent: "#ea580c" },
];
</script>

<style scoped>
.showcase-block {
  padding: 2.5rem 0;
  margin-top: 0.5rem;
}

.services-block {
  background: linear-gradient(
    180deg,
    rgba(0, 185, 255, 0.04) 0%,
    transparent 100%
  );
  border-top: 1px solid var(--border-color-hover1, #e5e7eb);
  border-bottom: 1px solid var(--border-color-hover1, #e5e7eb);
  padding-left: clamp(0.5rem, 2vw, 1.5rem);
  padding-right: clamp(0.5rem, 2vw, 1.5rem);
  border-radius: 20px;
}

.sports-block {
  padding-top: 3rem;
}

.section-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 2rem;
}

.section-kicker {
  margin: 0 0 0.65rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--border-color-hover);
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

h2 {
  margin: 0;
  font-size: clamp(1.55rem, 3.2vw, 2.15rem);
  color: var(--text-color1);
  font-weight: 800;
  line-height: 1.2;
}

.lead {
  margin: 0.85rem 0 0;
  color: var(--text-color1);
  opacity: 0.88;
  font-size: 1.02rem;
  line-height: 1.65;
}

.showcase-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}

.showcase-grid--sports {
  gap: 1.35rem;
}

.showcase-card {
  --media-height: min(52vw, 380px);
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-color-hover1, #e5e7eb);
  border-radius: 20px;
  background: var(--background-color2);
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
}

.showcase-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.14);
  border-color: var(--card-accent, var(--border-color-hover));
}

.showcase-card__media {
  position: relative;
  width: 100%;
  height: var(--media-height);
  min-height: 280px;
  flex-shrink: 0;
  overflow: hidden;
  background: #1a1a2e;
}

.showcase-card__media img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 280px;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;
}

.showcase-card:hover .showcase-card__media img {
  transform: scale(1.06);
}

.showcase-card__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.05) 0%,
    rgba(0, 0, 0, 0.15) 45%,
    rgba(0, 0, 0, 0.72) 100%
  );
  pointer-events: none;
}

.showcase-card__icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--card-accent, var(--border-color-hover));
  color: #fff;
  font-size: 1.1rem;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  z-index: 2;
}

.showcase-card__badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 2;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(255, 221, 87, 0.95);
  color: #1f2937;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.showcase-card__sport-label {
  position: absolute;
  left: 1rem;
  bottom: 1rem;
  z-index: 2;
  font-size: 1.35rem;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  letter-spacing: 0.02em;
}

.showcase-card__body {
  padding: 1.15rem 1.25rem 1.35rem;
  border-top: 3px solid var(--card-accent, var(--border-color-hover));
}

.showcase-card__body h3 {
  margin: 0 0 0.5rem;
  font-size: 1.12rem;
  font-weight: 700;
  color: var(--text-color1);
}

.showcase-card__body p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--text-color1);
  opacity: 0.86;
}

.showcase-card--sport .showcase-card__body h3 {
  color: var(--card-accent, var(--border-color-hover));
}

@media (min-width: 640px) {
  .showcase-card {
    --media-height: 340px;
  }

  .showcase-card__media {
    min-height: 340px;
  }

  .showcase-card__media img {
    min-height: 340px;
  }
}

@media (min-width: 900px) {
  .showcase-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }

  .showcase-card {
    --media-height: 400px;
  }

  .showcase-card__media {
    min-height: 400px;
  }

  .showcase-card__media img {
    min-height: 400px;
  }
}

@media (min-width: 1200px) {
  .showcase-grid--sports {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .showcase-grid:not(.showcase-grid--sports) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .showcase-card {
    --media-height: 420px;
  }

  .showcase-card__media {
    min-height: 420px;
  }

  .showcase-card__media img {
    min-height: 420px;
  }

  .showcase-grid--sports .showcase-card {
    --media-height: 360px;
  }

  .showcase-grid--sports .showcase-card__media,
  .showcase-grid--sports .showcase-card__media img {
    min-height: 360px;
  }
}
</style>
